import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Smartphone,
  Calendar,
  AlertTriangle,
  Trophy,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetailedStats();
  }, []);

  const fetchDetailedStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/detailed`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching detailed stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-600">Impossible de charger les statistiques</p>
      </div>
    );
  }

  const { basic_stats, top_devices, top_problems, technician_stats, monthly_trend } = stats;

  return (
    <div className="space-y-8 fade-in" data-testid="statistics-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Statistiques et Analytics</h1>
        <p className="text-slate-600 mt-1">Analyse détaillée des performances et tendances</p>
      </div>

      {/* Basic Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="glass card-hover">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{basic_stats.total_repairs}</p>
                <p className="text-sm font-medium text-slate-600">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass card-hover">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{basic_stats.en_cours}</p>
                <p className="text-sm font-medium text-slate-600">En cours</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass card-hover">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{basic_stats.termine}</p>
                <p className="text-sm font-medium text-slate-600">Terminées</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass card-hover">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{basic_stats.recupere}</p>
                <p className="text-sm font-medium text-slate-600">Récupérées</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass card-hover">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{basic_stats.monthly_repairs}</p>
                <p className="text-sm font-medium text-slate-600">Ce mois</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Devices */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Smartphone className="w-5 h-5" />
              <span>Appareils les plus réparés</span>
            </CardTitle>
            <CardDescription>Top 10 des appareils par volume de réparations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {top_devices.slice(0, 10).map((device, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                    <span className="font-medium text-slate-900 truncate max-w-xs" title={device.device}>
                      {device.device}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-32 bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min((device.count / (top_devices[0]?.count || 1)) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="font-bold text-slate-900 min-w-[2rem] text-right">
                      {device.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Problems */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5" />
              <span>Problèmes les plus fréquents</span>
            </CardTitle>
            <CardDescription>Top 10 des problèmes rencontrés</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {top_problems.slice(0, 10).map((problem, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                    <span className="font-medium text-slate-900 truncate max-w-xs" title={problem.problem}>
                      {problem.problem}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-32 bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-red-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min((problem.count / (top_problems[0]?.count || 1)) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="font-bold text-slate-900 min-w-[2rem] text-right">
                      {problem.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Technician Performance */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>Performance des techniciens</span>
          </CardTitle>
          <CardDescription>Nombre de réparations par technicien</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {technician_stats.map((tech, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {tech.technician.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{tech.technician}</p>
                      <p className="text-sm text-slate-600">Technicien</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-900">{tech.count}</p>
                    <p className="text-xs text-slate-500">réparations</p>
                  </div>
                </div>
                
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${Math.min((tech.count / (technician_stats[0]?.count || 1)) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trend */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5" />
            <span>Tendance mensuelle</span>
          </CardTitle>
          <CardDescription>Évolution du nombre de réparations sur les 6 derniers mois</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end space-x-4 h-64">
            {monthly_trend.map((month, index) => {
              const maxCount = Math.max(...monthly_trend.map(m => m.count));
              const height = maxCount > 0 ? (month.count / maxCount) * 200 : 0;
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div className="w-full flex flex-col items-center">
                    <div className="text-sm font-bold text-slate-900 mb-2">
                      {month.count}
                    </div>
                    <div 
                      className="w-full bg-gradient-to-t from-blue-500 to-indigo-500 rounded-t-lg transition-all duration-700"
                      style={{ height: `${height}px`, minHeight: month.count > 0 ? '8px' : '2px' }}
                    ></div>
                  </div>
                  <div className="text-xs text-slate-600 mt-3 transform -rotate-45 origin-left">
                    {new Date(month.month + '-01').toLocaleDateString('fr-FR', { 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="glass">
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Résumé des performances
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-blue-700">
                  {((basic_stats.termine + basic_stats.recupere) / basic_stats.total_repairs * 100 || 0).toFixed(1)}%
                </p>
                <p className="text-sm text-blue-600">Taux de réussite</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-green-700">
                  {basic_stats.monthly_repairs}
                </p>
                <p className="text-sm text-green-600">Réparations ce mois</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-purple-700">
                  {technician_stats.length}
                </p>
                <p className="text-sm text-purple-600">Techniciens actifs</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Statistics;